import { useState, useEffect, useRef } from "react";
import { Box, Stack, Typography, Alert, LinearProgress, Skeleton } from "@mui/joy";
import { useParams } from "react-router-dom";
import { Link } from "@/components/Link";
import { getIngredientsWithoutNutrition } from "../utils/ingredients";
import { calculateNutrition } from "../utils/nutrition";
import { capitalizeFirstLetter } from "../../../utils/strings";
import { useGetProfileQuery } from "@/features/onboarding/api";
import { calculateDailyIntake } from "@/features/settings/utils/dailyIntake";

export function NutritionPerServing({ recipe, sx }) {
  const { householdId } = useParams();
  const { data: profileData } = useGetProfileQuery();
  const unmatchedIngredients = getIngredientsWithoutNutrition(recipe);
  const groupedUnmatched = groupIngredientsByReason(unmatchedIngredients);
  const nutrition = calculateNutrition(recipe);  
  const containerRef = useRef(null);

  const showSkeleton = recipe.processed === false;
  const noNutritionData = !nutrition && recipe.processed !== false;

  const servings = recipe.servings;
  const profile = profileData?.data?.profile;  
  const nutritionProfile = profile?.nutrition_profile;

  const dailyIntake = calculateDailyIntake(nutritionProfile);

  const perServing = {
    kcal: Math.round(nutrition?.kcal / servings),
    protein: Math.round(nutrition?.protein / servings * 10) / 10,
    fat: Math.round(nutrition?.fat / servings * 10) / 10,
    carbs: Math.round(nutrition?.carbs / servings * 10) / 10,
    fiber: Math.round(nutrition?.fiber / servings * 10) / 10,
  };

  const proteinDailyPercentage = (perServing.protein / dailyIntake.protein) * 100;
  const fatDailyPercentage = (perServing.fat / dailyIntake.fat) * 100;
  const carbsDailyPercentage = (perServing.carbs / dailyIntake.carbs) * 100;
  const fiberDailyPercentage = (perServing.fiber / dailyIntake.fiber) * 100;

  const proteinCalories = perServing.protein * 4;
  const fatCalories = perServing.fat * 9;
  const carbsCalories = perServing.carbs * 4;
  const totalMainMacroCalories = proteinCalories + fatCalories + carbsCalories;
  
  const proteinMealPercentage = (proteinCalories / totalMainMacroCalories) * 100;
  const fatMealPercentage = (fatCalories / totalMainMacroCalories) * 100;
  const carbsMealPercentage = (carbsCalories / totalMainMacroCalories) * 100;
  const fiberMealPercentage = Math.min((perServing.fiber * 2 / perServing.kcal) * 100, 2);

  const [pieChartSize, setPieChartSize] = useState(140);
  const [nutritionBarSize, setNutritionBarSize] = useState('sm');
  const [gap, setGap] = useState(2);
  
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      const containerWidth = containerRef.current?.offsetWidth;
      if (!containerWidth) return;

      if (containerWidth < 368) {
        setPieChartSize(120);
        setNutritionBarSize('xs');
        setGap(1.5);
      } else {
        setPieChartSize(140);
        setNutritionBarSize('sm');
        setGap(2);
      }
    };
    
    updateSize();
    
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (noNutritionData) {
    return (
      <Box sx={{ backgroundColor: 'var(--joy-palette-neutral-100)', px: 1.5, py: 1.5, borderRadius: 'sm', ...sx }}>
        <Typography level="title-md" sx={{ fontWeight: '500', mb: 0.5 }}>Nutrition per Serving</Typography>
        <Typography level="body-sm" sx={{ color: 'var(--joy-palette-neutral-500)' }}>
          Nutrition data is not available for this recipe. Unable to match any ingredients.
        </Typography>
      </Box>
    );
  }

  return (
    <>      
      <Box ref={containerRef} sx={{ backgroundColor: 'var(--joy-palette-neutral-100)', px: 1.5, py: 1, borderRadius: 'sm', ...sx }}>
        {showSkeleton ? <Skeleton variant="text" level="title-md" sx={{ width: '160px', mb: 1 }} /> : (
          <Typography level="title-md" sx={{ fontWeight: '500', mb: 1 }}>Nutrition per Serving</Typography>        
        )}
        <Stack direction="row" gap={gap} sx={{ py: 1}} justifyContent="space-between" alignItems="center">          
          {showSkeleton ? <Skeleton variant="circular" width={pieChartSize} height={pieChartSize} /> : (
            <CaloriesBar 
              size={pieChartSize}
              value={perServing.kcal} 
              proteinPercentage={proteinMealPercentage}
              fatPercentage={fatMealPercentage}
              carbsPercentage={carbsMealPercentage}
              fiberPercentage={fiberMealPercentage}
            />)
          }
          <Stack direction="column" gap={1} sx={{ flex: 1, minWidth: 0, mt: -0.75 }}>            
            <NutritionBar label="Fat" emoji="🧈"value={perServing.fat} dailyPercentage={fatDailyPercentage} color="warning" skeleton={showSkeleton} size={nutritionBarSize} />
            <NutritionBar label="Carbs" emoji="🧁" value={perServing.carbs} dailyPercentage={carbsDailyPercentage} color="danger" skeleton={showSkeleton} size={nutritionBarSize} />
            <NutritionBar label="Protein" emoji="💪🏼" value={perServing.protein} dailyPercentage={proteinDailyPercentage} color="primary" skeleton={showSkeleton} size={nutritionBarSize} />
            <NutritionBar label="Fiber" emoji="🌾" value={perServing.fiber} dailyPercentage={fiberDailyPercentage} color="success" skeleton={showSkeleton} size={nutritionBarSize} />
          </Stack>
        </Stack>        
        {showSkeleton ? <Skeleton variant="rectangular" sx={{ width: '100%', height: '44px', mt: 1.5, mb: 0.5, borderRadius: 'sm' }} /> : (
        <Alert color="info" sx={{ mt: 1.5, mb: 0.5 }}>            
          <Box sx={{ pr: 1.25, fontSize: '13px' }}>
            Percentages are based on {dailyIntake.isDefault ? 'recommended daily intake of an average adult' : 'your recommended daily intake'}. 
            {dailyIntake.isDefault && <Box sx={{ mt: 1 }}>
              <Link to={`/h/${householdId}/settings/personal?addNutritionProfile=true`} sx={{ fontWeight: '500' }}>Personalize your nutrition profile</Link>
            </Box>}
          </Box>            
          <Box sx={{ fontWeight: '400', color: 'var(--joy-palette-neutral-400)', position: 'absolute', top: '0.75em', right: '0.75em' }}><sup>*</sup></Box>
        </Alert>)}        
      </Box>
      {unmatchedIngredients.length > 0 && !showSkeleton && (
        <Alert color="warning">
          <Box sx={{ width: '100%' }}>
            Some ingredients are not included in the nutrition info
            <Stack direction="column" gap={1} sx={{ mt: 1.5 }}>
            {groupedUnmatched.map(({ reason, ingredients }) => (
              <Box key={reason} component="div" sx={{ backgroundColor: 'var(--joy-palette-warning-50)', p: 0.5, px: 1, borderRadius: 3, borderLeft: '2px solid var(--joy-palette-warning-300)'}}>      
                <Box style={{ fontSize: '12px', color: 'var(--joy-palette-warning-700)' }}>
                  {getReasonLabel(reason)}
                </Box>          
                <em style={{ fontWeight: '300' }}>
                  {ingredients.map(ingredient => capitalizeFirstLetter(ingredient.name)).join(', ')}
                </em>                
              </Box>
            ))}
            </Stack>
          </Box>
        </Alert>
      )}
    </>
  );
}

function CaloriesBar({ size, value, proteinPercentage, fatPercentage, carbsPercentage, fiberPercentage }) {
  return (    
    <Box>
      <MultiSegmentCircularProgress
        size={size}
        proteinPercentage={proteinPercentage}
        fatPercentage={fatPercentage}
        carbsPercentage={carbsPercentage}
        fiberPercentage={fiberPercentage}
      >
        <Stack direction="column" gap={0} sx={{ textAlign: 'center' }}>
          <Typography level="body-sm" sx={{ fontWeight: '400', color: 'var(--joy-palette-neutral-600)', fontSize: (size < 140) ? '13px' : '14px' }}>Calories</Typography>
          <Typography level="body-md" sx={{ fontWeight: '500', fontSize: (size < 140) ? '15px' : '16px' }}>🔥{value} kcal</Typography>
        </Stack>
      </MultiSegmentCircularProgress>    
    </Box>
  );
}

function MultiSegmentCircularProgress({ size, proteinPercentage, fatPercentage, carbsPercentage, fiberPercentage, children }) {
  
  const thickness = 4;
  const radius = (size - thickness) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const gapSize = 5;
  const totalGaps = gapSize * 4;
  const availableCircumference = circumference - totalGaps;
  
  const mainMacrosTotal = proteinPercentage + fatPercentage + carbsPercentage;
  const totalPercentage = mainMacrosTotal + fiberPercentage;
  
  const proteinLength = (proteinPercentage / totalPercentage) * availableCircumference;
  const fatLength = (fatPercentage / totalPercentage) * availableCircumference;
  const carbsLength = (carbsPercentage / totalPercentage) * availableCircumference;
  const fiberLength = (fiberPercentage / totalPercentage) * availableCircumference;

  const createArc = (startAngle, endAngle) => {
    const start = {
      x: center + radius * Math.cos(startAngle),
      y: center + radius * Math.sin(startAngle),
    };
    const end = {
      x: center + radius * Math.cos(endAngle),
      y: center + radius * Math.sin(endAngle),
    };
    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
  };

  const toRadians = (offset) => (offset / circumference) * 2 * Math.PI - Math.PI / 2;


  const fatStart = gapSize;
  const fatEnd = fatStart + fatLength;
  const carbsStart = fatEnd + gapSize;
  const carbsEnd = carbsStart + carbsLength;
  const proteinStart = carbsEnd + gapSize;
  const proteinEnd = proteinStart + proteinLength;
  const fiberStart = proteinEnd + gapSize;
  const fiberEnd = fiberStart + fiberLength;

  const proteinColor = "var(--joy-palette-primary-400)";
  const fatColor = "var(--joy-palette-warning-400)";
  const carbsColor = "var(--joy-palette-danger-400)";
  const fiberColor = "var(--joy-palette-success-400)";
  const trackColor = "var(--joy-palette-neutral-200)";

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width={size}
        height={size}
        style={{ position: 'absolute' }}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={thickness}
        />
        {proteinPercentage > 0 && (
          <path
            d={createArc(toRadians(proteinStart), toRadians(proteinEnd))}
            fill="none"
            stroke={proteinColor}
            strokeWidth={thickness}
            strokeLinecap="round"
          />
        )}
        {fatPercentage > 0 && (
          <path
            d={createArc(toRadians(fatStart), toRadians(fatEnd))}
            fill="none"
            stroke={fatColor}
            strokeWidth={thickness}
            strokeLinecap="round"
          />
        )}
        {carbsPercentage > 0 && (
          <path
            d={createArc(toRadians(carbsStart), toRadians(carbsEnd))}
            fill="none"
            stroke={carbsColor}
            strokeWidth={thickness}
            strokeLinecap="round"
          />
        )}
        {fiberPercentage > 0 && (
          <path
            d={createArc(toRadians(fiberStart), toRadians(fiberEnd))}
            fill="none"
            stroke={fiberColor}
            strokeWidth={thickness}
            strokeLinecap="round"
          />
        )}
      </svg>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {children}
      </Box>
    </Box>
  );
}

function NutritionBar({ label, emoji, value, dailyPercentage, color, skeleton, size }) {
  const iconBgSize = size === 'xs' ? 22 : 28;
  const iconSize = size === 'xs' ? 16 : 18;
  const fontSize = size === 'xs' ? '13px' : '14px';
  const dailyPercentageFontSize = size === 'xs' ? '11px' : '12px';
  const gap = size === 'xs' ? 0.75 : 1;

  return (
    <Stack direction="row" gap={gap} alignItems="center" sx={{ width: '100%' }}>
      {skeleton ? <Skeleton variant="circular" width={iconBgSize} height={iconBgSize} sx={{ position: 'relative', top: '2px' }} /> : (
        <Box sx={{ fontSize: iconSize, backgroundColor: 'var(--joy-palette-neutral-200)', width: iconBgSize, height: iconBgSize, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', top: '2px' }}><span>{emoji}</span></Box>
      )}      
      <Box sx={{ flex: 1 }}>                
        <Stack direction="row" gap={0} sx={{ mb: 0 }} justifyContent="space-between">
          {skeleton ? <Skeleton variant="text" level="body-sm" sx={{ width: '50px', fontSize: fontSize }} /> : (
            <Typography level="body-sm" sx={{ fontWeight: '500', color: 'var(--joy-palette-neutral-600)', fontSize: fontSize }}>{label}</Typography>        
          )}
          {skeleton ? 
            <Skeleton variant="text" level="body-sm" sx={{ width: '50px', mt: '2.66px', fontSize: fontSize }} /> : <Stack direction="row" gap={0.5} alignItems="center">
              <Typography level="body-sm" sx={{ fontWeight: '400', fontSize: fontSize }}>{value}g <span style={{ color: 'var(--joy-palette-neutral-400)', fontSize: dailyPercentageFontSize }}>({Math.round(dailyPercentage)}%)<sup>*</sup></span></Typography>            
            </Stack>          
          }
        </Stack>
        
        {skeleton ? <Skeleton variant="rectangular" height={4} sx={{ borderRadius: 'sm', width: '100%' }} /> : (
        <LinearProgress value={Math.min(100, Math.max(1, dailyPercentage))} determinate variant="solid" color="neutral" sx={{        
          "&::before": { 
            backgroundColor: 
              color === "primary" ? "var(--joy-palette-primary-400)" : 
              color === "warning" ? "var(--joy-palette-warning-400)" : 
              color === "danger" ? "var(--joy-palette-danger-400)" : 
              color === "success" ? "var(--joy-palette-success-400)" : "var(--joy-palette-neutral-400)" }
        }} size="sm" />)}         
      </Box>
    </Stack>
  );
}

function groupIngredientsByReason(unmatchedIngredients) {
  const grouped = {};
  unmatchedIngredients.forEach(({ ingredient, reason }) => {
    if (!grouped[reason]) {
      grouped[reason] = [];
    }
    grouped[reason].push(ingredient);
  });
  return Object.entries(grouped).map(([reason, ingredients]) => ({ reason, ingredients }));
}

function getReasonLabel(reason) {
  switch (reason) {
    case 'not_matched':
      return 'No matching ingredients';
    case 'missing_piece_weight':
      return 'Ingredients without piece weight';
    case 'missing_slice_weight':
      return 'Ingredients without slice weight';
    default:
      return 'Unknown reason';
  }
}
